/* ==========================================================
   VECTOR COMPONENT VC-009
   DELIVERY PROMISE & COUNTDOWN
   FINAL VERSION 1.0
   ========================================================== */

class VectorDelivery {
  constructor(root) {
    this.root = root;
    this.primary = root.querySelector(
      '[data-vector-delivery-primary]'
    );
    this.secondary = root.querySelector(
      '[data-vector-delivery-secondary]'
    );

    this.cutoffHour = Number.parseInt(
      root.dataset.cutoffHour || '19',
      10
    );

    this.timeZone = root.dataset.timeZone || 'Europe/London';

    if (!this.primary || !this.secondary) {
      return;
    }

    this.render();
    this.timer = window.setInterval(() => this.render(), 30000);
  }

  getLondonParts(date = new Date()) {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });

    const parts = {};

    formatter.formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') {
        parts[part.type] = part.value;
      }
    });

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      weekday: parts.weekday,
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second)
    };
  }

  createDay(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  dateKey(date) {
    return [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0')
    ].join('-');
  }

  addDays(date, amount) {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + amount);
    return next;
  }

  easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return this.createDay(year, month, day);
  }

  firstMonday(year, month) {
    let date = this.createDay(year, month, 1);

    while (date.getUTCDay() !== 1) {
      date = this.addDays(date, 1);
    }

    return date;
  }

  lastMonday(year, month) {
    let date = this.createDay(year, month + 1, 0);

    while (date.getUTCDay() !== 1) {
      date = this.addDays(date, -1);
    }

    return date;
  }

  substituteDate(date) {
    const day = date.getUTCDay();

    if (day === 6) {
      return this.addDays(date, 2);
    }

    if (day === 0) {
      return this.addDays(date, 1);
    }

    return date;
  }

  englandWalesHolidays(year) {
    const holidays = new Set();

    const addHoliday = (date) => {
      holidays.add(this.dateKey(date));
    };

    /* New Year */
    addHoliday(
      this.substituteDate(this.createDay(year, 1, 1))
    );

    /* Easter */
    const easter = this.easterSunday(year);
    addHoliday(this.addDays(easter, -2));
    addHoliday(this.addDays(easter, 1));

    /* May and August holidays */
    addHoliday(this.firstMonday(year, 5));
    addHoliday(this.lastMonday(year, 5));
    addHoliday(this.lastMonday(year, 8));

    /* Christmas and Boxing Day substitute handling */
    const christmas = this.createDay(year, 12, 25);
    const boxingDay = this.createDay(year, 12, 26);

    if (christmas.getUTCDay() === 6) {
      addHoliday(this.addDays(christmas, 2));
      addHoliday(this.addDays(boxingDay, 2));
    } else if (christmas.getUTCDay() === 0) {
      addHoliday(this.addDays(christmas, 2));
      addHoliday(this.addDays(boxingDay, 2));
    } else if (boxingDay.getUTCDay() === 6) {
      addHoliday(christmas);
      addHoliday(this.addDays(boxingDay, 2));
    } else if (boxingDay.getUTCDay() === 0) {
      addHoliday(christmas);
      addHoliday(this.addDays(boxingDay, 2));
    } else {
      addHoliday(christmas);
      addHoliday(boxingDay);
    }

    return holidays;
  }

  isWorkingDay(date) {
    const weekday = date.getUTCDay();

    if (weekday === 0 || weekday === 6) {
      return false;
    }

    const holidays = this.englandWalesHolidays(
      date.getUTCFullYear()
    );

    return !holidays.has(this.dateKey(date));
  }

  nextWorkingDay(date, includeCurrent = false) {
    let candidate = includeCurrent
      ? new Date(date.getTime())
      : this.addDays(date, 1);

    while (!this.isWorkingDay(candidate)) {
      candidate = this.addDays(candidate, 1);
    }

    return candidate;
  }

  formatDay(date, includeDate = false) {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'long',
      ...(includeDate
        ? {
            day: 'numeric',
            month: 'long'
          }
        : {})
    }).format(date);
  }

  formatCountdown(milliseconds) {
    const totalSeconds = Math.max(
      0,
      Math.floor(milliseconds / 1000)
    );

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    return `${String(hours).padStart(2, '0')}h ${String(
      minutes
    ).padStart(2, '0')}m`;
  }

  render() {
    const now = this.getLondonParts();
    const today = this.createDay(now.year, now.month, now.day);

    const currentWallTime = Date.UTC(
      now.year,
      now.month - 1,
      now.day,
      now.hour,
      now.minute,
      now.second
    );

    const cutoffWallTime = Date.UTC(
      now.year,
      now.month - 1,
      now.day,
      this.cutoffHour,
      0,
      0
    );

    const todayIsWorking = this.isWorkingDay(today);
    const beforeCutoff =
      todayIsWorking && currentWallTime < cutoffWallTime;

    let dispatchDate;

    if (beforeCutoff) {
      dispatchDate = today;

      const remaining = cutoffWallTime - currentWallTime;

      this.root.classList.remove('is-after-cutoff');

      this.primary.innerHTML =
        `Order within ` +
        `<strong class="vector-delivery__countdown">` +
        `${this.formatCountdown(remaining)}` +
        `</strong> for dispatch today`;
    } else {
      dispatchDate = this.nextWorkingDay(today);

      this.root.classList.add('is-after-cutoff');

      this.primary.innerHTML =
        `Order now for dispatch ` +
        `<strong>${this.formatDay(dispatchDate)}</strong>`;
    }

    const deliveryDate = this.nextWorkingDay(dispatchDate);

    this.secondary.textContent =
      `Estimated delivery: ${this.formatDay(
        deliveryDate,
        true
      )} · UK working days only`;
  }
}

function initialiseVectorDelivery(root = document) {
  root
    .querySelectorAll('[data-vector-delivery]')
    .forEach((element) => {
      if (element.dataset.vectorDeliveryReady === 'true') {
        return;
      }

      element.dataset.vectorDeliveryReady = 'true';
      new VectorDelivery(element);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  initialiseVectorDelivery();
});

document.addEventListener('shopify:section:load', (event) => {
  initialiseVectorDelivery(event.target);
});

/* ==========================================================
   END VC-009
   ========================================================== */